import { useState } from "react";
import { ArrowUpRight, Check, Loader2 } from "lucide-react";
import { CONTACT_EMAIL } from "@/data/site";

// Preview: backend URL assoluto da .env. Pacchetto statico: percorso relativo
// alla sottocartella di installazione (PUBLIC_URL), così le chiamate restano same-origin.
const API = process.env.REACT_APP_BACKEND_URL || process.env.PUBLIC_URL || "";

const inputClass =
  "w-full bg-transparent border-b border-black/20 focus:border-[#FF6B1A] outline-none py-3 text-base text-[#121212] placeholder:text-[#121212]/35 transition-colors";

export default function Contact() {
  const [form, setForm] = useState({ full_name: "", company: "", email: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          project_type: "altra_richiesta",
          privacy_consent: consent,
        }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        const detail = typeof d.detail === "string" ? d.detail : null;
        throw new Error(detail || "Impossibile inviare la richiesta al momento.");
      }
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(err.message || "Impossibile inviare la richiesta al momento.");
    }
  };

  return (
    <section data-testid="contact-section" className="relative py-24 md:py-36 bg-[#f7f5f0] border-t border-black/[0.08]">
      <div className="max-w-[1280px] mx-auto px-5 md:px-8">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="label-eyebrow mb-5 flex items-center gap-3">
              <span>IV.</span>
              <span className="w-5 h-px bg-black/25" />
              <span>Parliamone</span>
            </div>
            <h2 className="font-display tighter text-[#121212] text-5xl md:text-6xl lg:text-[4.4vw] leading-[1.02] font-normal">
              Diamo spazio al tuo
              <br />
              <span className="italic text-[#FF6B1A]">prossimo progetto</span> automotive.
            </h2>
            <p className="mt-8 text-[#121212]/60 text-base leading-relaxed max-w-md">
              Compila il modulo: la richiesta arriva direttamente alla nostra redazione. Oppure scrivici a{" "}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#FF6B1A] hover:underline" data-testid="contact-email-link">
                {CONTACT_EMAIL}
              </a>
            </p>
          </div>

          <div className="lg:col-span-6 lg:pt-16">
            {status === "success" ? (
              <div data-testid="contact-success-message" className="border border-black/10 rounded-xl bg-white p-8 md:p-10">
                <div className="w-12 h-12 rounded-full bg-[#FF6B1A]/10 flex items-center justify-center mb-5">
                  <Check size={22} className="text-[#FF6B1A]" />
                </div>
                <p className="font-display text-2xl text-[#121212]">Richiesta inviata.</p>
                <p className="mt-3 text-[#121212]/60 text-base leading-relaxed">
                  Grazie! Abbiamo ricevuto il tuo messaggio e ti risponderemo al più presto da {CONTACT_EMAIL}.
                </p>
              </div>
            ) : (
              <form data-testid="contact-form" onSubmit={submit} className="space-y-7">
                <div className="grid sm:grid-cols-2 gap-7">
                  <div>
                    <label htmlFor="contact-name" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#121212]/50 block mb-1">
                      Nome e cognome *
                    </label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      type="text"
                      required
                      minLength={2}
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="Mario Rossi"
                      className={inputClass}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-company" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#121212]/50 block mb-1">
                      Azienda *
                    </label>
                    <input
                      id="contact-company"
                      data-testid="contact-company-input"
                      type="text"
                      required
                      value={form.company}
                      onChange={update("company")}
                      placeholder="Concessionaria, brand, agenzia…"
                      className={inputClass}
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="contact-email" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#121212]/50 block mb-1">
                    La tua email *
                  </label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={update("email")}
                    placeholder="nome@azienda.it"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="contact-message" className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#121212]/50 block mb-1">
                    Messaggio *
                  </label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    rows={4}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Raccontaci il tuo progetto o la tua richiesta…"
                    className={`${inputClass} resize-none`}
                  />
                </div>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    data-testid="contact-consent-checkbox"
                    required
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#FF6B1A]"
                  />
                  <span className="text-sm text-[#121212]/60 leading-relaxed">
                    Ho letto la{" "}
                    <a href="https://www.deltapictures.it/privacy-policy/" target="_blank" rel="noreferrer noopener" className="underline hover:text-[#FF6B1A]">
                      privacy policy
                    </a>{" "}
                    e acconsento al trattamento dei dati per essere ricontattato. *
                  </span>
                </label>
                {status === "error" && (
                  <p data-testid="contact-error-message" className="text-sm text-red-600">
                    {errorMsg} Puoi anche scriverci direttamente a {CONTACT_EMAIL}.
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={status === "sending"}
                  className="btn-primary disabled:opacity-60 disabled:pointer-events-none"
                >
                  {status === "sending" ? (
                    <>Invio in corso <Loader2 size={16} className="animate-spin" /></>
                  ) : (
                    <>Invia la richiesta <ArrowUpRight size={16} className="arrow" /></>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
